import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Outcome {
  category: string;
  outcomes: string[];
}

interface ExpectedOutcomesProps {
  outcomes: Outcome[];
}

const ExpectedOutcomes: React.FC<ExpectedOutcomesProps> = ({ outcomes }) => {
  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-4 sm:mb-6">
              Expected Outcomes & Success Metrics
            </h2>
            <p className="text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-2">
              Measurable institutional transformation across strategic clarity, leadership capability, and execution readiness.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {outcomes.map((outcome, index) => (
              <div
                key={index}
                className="bg-surface rounded-lg institutional-border p-6 sm:p-8 hover:institutional-shadow transition-base"
              >
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-institutional/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircleIcon" size={18} className="text-institutional sm:hidden" />
                    <Icon name="CheckCircleIcon" size={20} className="text-institutional hidden sm:block" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-text-primary">
                    {outcome.category}
                  </h3>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {outcome.outcomes.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="w-1.5 h-1.5 bg-institutional rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                      <span className="text-xs sm:text-sm text-text-secondary leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 bg-institutional/5 rounded-lg institutional-border p-6 sm:p-8 lg:p-12">
            <div className="flex items-start gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-institutional/10 rounded-md flex items-center justify-center">
                  <Icon name="ChartBarIcon" size={22} className="text-institutional sm:hidden" />
                  <Icon name="ChartBarIcon" size={24} className="text-institutional hidden sm:block" />
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-3 sm:mb-4">
                  Long-term Institutional Impact
                </h3>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed mb-3 sm:mb-4">
                  Beyond immediate deliverables, the program builds sustainable institutional capabilities that continue to generate value long after the formal engagement concludes. Organizations develop internal capacity for strategic thinking, systematic execution, and continuous improvement.
                </p>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
                  Success is measured not only by completed frameworks and trained leaders, but by the institution's demonstrated ability to navigate future challenges with greater strategic clarity and execution confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpectedOutcomes;